import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { useField } from 'formik';

import { PlusIcon } from '../../../components/Dialogs/CollabsFilters/ToolTip';
import SelectOption from '../../../components/Formiks/SelectOption/SelectOption';
import { OfferAccessType } from '../../../graphql/API';
import { useAppSelector } from '../../../redux/hooks';
import { memberAccessOptions } from '../../../utils/offerAccess';
import { extractSubscriptionPlan } from '../../SubscriptionPlans/common/helper';
import SideBySide from './SideBySide';

const OfferAccessSimplified: React.FC = () => {
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const [, selectMeta, selectHelper] = useField('selectMembersAccess');
  const [, lifestyleMeta, lifestyleHelper] = useField('lifestyleMembersAccess');

  const isPlusPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');
    return !!plan?.includes('plus');
  }, [subscription]);

  const [mode, setMode] = React.useState<'open' | 'selection'>(
    selectMeta.value !== OfferAccessType.acceptAll ||
      lifestyleMeta.value !== OfferAccessType.acceptAll
      ? 'selection'
      : 'open',
  );

  const handleSelectOpen = () => {
    setMode('open');
    selectHelper.setValue(OfferAccessType.acceptAll);
    lifestyleHelper.setValue(OfferAccessType.acceptAll);
  };

  const handleSelectSelection = () => {
    if (!isPlusPlan) return;
    setMode('selection');
    if (
      selectMeta.value === OfferAccessType.acceptAll &&
      lifestyleMeta.value === OfferAccessType.acceptAll
    ) {
      selectHelper.setValue(OfferAccessType.acceptAll);
      lifestyleHelper.setValue(OfferAccessType.permissionRequired);
    }
  };

  const accessOptions = memberAccessOptions.slice(0, 2);

  return (
    <Box>
      <Typography sx={{ fontSize: '22px', fontWeight: 600, mb: 3 }}>Offer access</Typography>
      <Stack spacing={2}>
        <AccessCard active={mode === 'open'} onClick={handleSelectOpen}>
          <CardTitle active={mode === 'open'}>Automatic booking</CardTitle>
          <CardDescription>
            Users who meet your requirements can book automatically.
          </CardDescription>
        </AccessCard>

        <AccessCard
          active={mode === 'selection'}
          disabled={!isPlusPlan}
          onClick={handleSelectSelection}
        >
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
            <CardTitle active={mode === 'selection'} dimmed={!isPlusPlan}>
              Approve requests
            </CardTitle>
            {!isPlusPlan && (
              <Box
                component={Link}
                to='/plans'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textDecoration: 'none',
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <PlusIcon />
                <Typography
                  component='span'
                  sx={{
                    color: '#D86EEF',
                    fontWeight: 500,
                    fontSize: '13px',
                    textDecoration: 'underline',
                  }}
                >
                  PLUS
                </Typography>
              </Box>
            )}
          </Stack>
          <CardDescription dimmed={!isPlusPlan}>
            Users request access first. You choose who can book.
          </CardDescription>
        </AccessCard>

        {mode === 'selection' && isPlusPlan && (
          <SideBySide>
            <FieldBox onClick={(e) => e.stopPropagation()}>
              <FieldLabel>Beautypass Select</FieldLabel>
              <SelectOption
                id='slt-access-select'
                name='selectMembersAccess'
                options={accessOptions}
                fullWidth
                sx={{ padding: '5px 0' }}
              />
            </FieldBox>
            <FieldBox onClick={(e) => e.stopPropagation()}>
              <FieldLabel>Lifestyle</FieldLabel>
              <SelectOption
                id='slt-access-lifestyle'
                name='lifestyleMembersAccess'
                options={accessOptions}
                fullWidth
                sx={{ padding: '5px 0' }}
              />
            </FieldBox>
          </SideBySide>
        )}
      </Stack>
    </Box>
  );
};

export default OfferAccessSimplified;

const AccessCard = styled.div<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 20px;
  border-radius: 10px;
  border: ${(props) => (props.active ? '2px solid #111' : '1px solid #e6e6e6')};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`;

const CardTitle = styled.p<{ active?: boolean; dimmed?: boolean }>`
  font-size: 15px;
  font-weight: ${(props) => (props.active ? '600' : '400')};
  font-family: Poppins;
  color: #111;
  margin: 0;
  padding: 0;
  opacity: ${(props) => (props.dimmed ? 0.6 : 1)};
`;

const CardDescription = styled.p<{ dimmed?: boolean }>`
  color: #848484;
  font-family: Poppins;
  font-size: 13px;
  margin: 0;
  padding: 0;
  opacity: ${(props) => (props.dimmed ? 0.6 : 1)};
`;

const FieldBox = styled.div`
  display: flex;
  flex-direction: column;
  fieldset {
    border: 1px solid #e6e6e6 !important;
  }
`;

const FieldLabel = styled.label`
  font-size: 15px;
  font-weight: 500;
  font-family: Poppins;
  color: #111;
  margin-bottom: 8px;
`;
